import { useFormik } from 'formik';
import { FC } from 'react';
import { FaEuroSign } from 'react-icons/fa6';
import { HiOutlineInformationCircle } from 'react-icons/hi2';
import { useNavigate } from 'react-router-dom';
import { cn, Textarea } from 'rizzui';
import { toast } from 'sonner';
import * as yup from 'yup';
import CustomSelect from '../../components/forms/CustomSelect';
import CustomSelectApiHookForm from '../../components/forms/CustomSelectApiHookForms';
import FormLabel from '../../components/forms/FormLabel';
import RequiredInput from '../../components/forms/InputHOC';
import StatusDropdown from '../../components/forms/StatusDropdown';
import { usePrivilege } from '../../components/priviledge/PriviledgeProvider';
import useContract from '../../hooks/api-calls/contract/useContracts';
import useExpirationStates from '../../hooks/api-calls/expirations/useExpirationStates';
import useKoReasons from '../../hooks/api-calls/useKoReasons';
import usePaymentMethods from '../../hooks/api-calls/expirations/usePaymentMethods';
import useInvoices from '../../hooks/api-calls/invoices/useInvoices';
import useUsers from '../../hooks/api-calls/useUsers';
import useHandleErrors from '../../hooks/useHandleErrors';
import Page from '../../layout/Page/Page';
import FormGroup from '../../layout/shared/form-group';
import { ExpirationService } from '../../services/expiration/expirationService';
import FormFooter from '../_layout/_footers/form-footer';

interface ExpirationFormProps {
    data?: any;
    submit: (values: any) => void;
    isLoading: boolean;
}

const initialValues = {
    invoiceId: '',
    contractId: '',
    date: '',
    amount: 0,
    description: '',
    paymentMethod: '',
    reminderDate: '',
    reminderResponsibleId: '',
    notes: '',
    deleted: false,
};

export const expirationSchema = yup.object({
    invoiceId: yup.string().required('La factura es obligatoria'),
    contractId: yup.string().required('El contrato es obligatorio'),
    date: yup.string().required('La fecha de vencimiento es obligatoria'),
    amount: yup.number().required('El importe es obligatorio'),
    description: yup.string().required('La descripción es obligatoria'),
    paymentMethodId: yup.string().required('El método de pago es obligatorio'),
    reminderDate: yup.string()
        .nullable()
        .test('reminderDateRequiredWithResponsible', 'La fecha de recordatorio es obligatoria si hay responsable', function (value) {
            const { reminderResponsibleId } = this.parent;
            if (reminderResponsibleId && !value) {
                return false;
            }
            return true;
        }),
    reminderResponsibleId: yup.string()
        .nullable()
        .test('responsibleRequiredWithDate', 'El responsable del recordatorio es obligatorio si hay fecha de recordatorio', function (value) {
            const { reminderDate } = this.parent;
            if (reminderDate && !value) {
                return false;
            }
            return true;
        }),
    notes: yup.string().nullable().notRequired(),
    deleted: yup.boolean().notRequired(),
});

const ExpirationForm: FC<ExpirationFormProps> = ({ data, submit, isLoading }) => {

    // HOOKS

    const mode = data ? "Editar" : "Crear";
    const navigate = useNavigate();
    const { userCan } = usePrivilege();
    const { handleErrors } = useHandleErrors();
    const { fetchInvoices } = useInvoices({ active: true }, true);
    const { fetchContracts } = useContract({ active: true }, true);
    const { getUsersList } = useUsers({ active: true }, true);
    const { getPaymentMethodsList } = usePaymentMethods(true);
    const { getExpirationStatesList } = useExpirationStates();
    const { koReasonsOptions } = useKoReasons();

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR ESTADO
     * @EN CHANGE STATUS
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string, koReasonId?: string) => {
        var response = { success: false };
        try {
            response = await (await (new ExpirationService()).changeExpirationStatus(id, stateId, comment, koReasonId)).getResponseData();
            if (response.success) {
                toast.success('Estado del vencimiento actualizado correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            toast.error('Error al actualizar el estado');
        }
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    const formik = useFormik({
        initialValues: {
            
            ...initialValues,
            ...(data && {
                id: data ? data.id : null,
                invoiceId: data.invoice?.id,
                invoiceNumber: data.invoice?.number, // only used to display the name in the selector
                contractId: data.contract?.id,
                contractCode: data.contract?.code, // only used to display the name in the selector
                date: data.expirationDate?.date.split(' ')[0],
                amount: data.amount,
                description: data.description,
                paymentMethodId: data.paymentMethod?.id,
                reminderDate: data.reminderDate ? data.reminderDate?.date.split(' ')[0] : null,
                reminderResponsibleId: data.reminderResponsible?.id,
                notes: data.notes,
                deleted: data.isDeleted,
            }),
        },
        validationSchema: expirationSchema,
        validateOnBlur: false,
        validateOnChange: false,
        onSubmit: values => {
            const { invoiceId, contractId, ...expirationData } = values;
            const payload = {
                invoiceId,
                contractId,
                expirations: [expirationData],
            };
            submit(payload);
        },
    });

    // RENDER

    const getContent = () => {
        return (
            <>
                <div className="@container">
                    <form onSubmit={formik.handleSubmit}>
                        <div className="grid gap-7 divide-y-2 divide-dashed divide-gray-200 font-medium">
                            <FormGroup
                                title="Información General"
                                description="Datos principales"
                                titleCols="@md:col-span-3"
                                childCols={cn("@md:col-span-9 md:grid-cols-2", { 'md:grid-cols-3': mode === "Editar" })}
                            >
                                <div className="space-y-2">
                                    <CustomSelectApiHookForm
                                        id="invoiceId"
                                        label="Factura"
                                        formik={formik}
                                        fetchOptions={async (searchText: string | null) => {
                                            const text = searchText ? searchText : formik.values.invoiceNumber;
                                            return fetchInvoices({ search_text: text }) as any;
                                        }}
                                        error={formik.errors.invoiceId}
                                        isDisabled={mode === "Editar" || !userCan('get_invoices_for_select', 'invoices')}
                                        minInputLength={5}
                                    />
                                    {!userCan('get_invoices_for_select', 'invoices') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver las facturas</span>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <CustomSelectApiHookForm
                                        id="contractId"
                                        label="Contrato"
                                        formik={formik}
                                        fetchOptions={async (searchText: string | null) => {
                                            const text = searchText ? searchText : formik.values.contractCode;
                                            return fetchContracts({ search_text: text }) as any;
                                        }}
                                        error={formik.errors.contractId}
                                        isDisabled={mode === "Editar" || (!userCan('list_contracts', 'contracts') && !userCan('admin_contracts', 'contracts'))}
                                        minInputLength={5}
                                    />
                                    {!userCan('list_contracts', 'contracts') && !userCan('admin_contracts', 'contracts') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los contratos</span>
                                        </div>
                                    )}
                                </div>

                                {data?.lastExpirationKoReason && (
                                    <div className="md:col-span-2">
                                        <FormLabel label="Motivo de KO" />
                                        <div className="flex flex-col gap-1 p-2 bg-gray-50 rounded border">
                                            <span className="font-medium">{data.lastExpirationKoReason.name}</span>
                                            {data.lastExpirationKoReason.description && (
                                                <span className="text-sm text-gray-600">{data.lastExpirationKoReason.description}</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {data && (
                                    <div>
                                        <FormLabel label='Estado' />
                                        <StatusDropdown
                                            disabled={!userCan('change_expiration_statuses', 'expirations')}
                                            entityId={data?.id || ''}
                                            title="¿Estás seguro de cambiar el estado del vencimiento?"
                                            statesOptions={getExpirationStatesList(data?.currentDueStatus.id)}
                                            currentState={data?.currentDueStatus}
                                            currentStateDate={data?.currentDueStatus.date}
                                            koReasonsOptions={koReasonsOptions}
                                            koReasonStatusEntity="expiration"
                                            handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) => handleStateChange(entityId, statusId, comment, koReasonId)}
                                        />
                                    </div>
                                )}
                            </FormGroup>

                            <FormGroup
                                title="Vencimiento"
                                description="Datos del vencimiento"
                                className='pt-6'
                                titleCols="@md:col-span-12"
                                childCols="@md:col-span-12 md:grid-cols-5"
                            >
                                <RequiredInput
                                    id='description'
                                    label="Descripción"
                                    className="md:col-span-2"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='amount'
                                    type="number"
                                    label="Importe"
                                    formik={formik}
                                    suffix={<FaEuroSign />}
                                />

                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id={'paymentMethodId'}
                                        label="Método de Pago"
                                        required
                                        options={getPaymentMethodsList()}
                                        onChange={(e: any) => formik.setFieldValue('paymentMethodId', e ? e.value : '')}
                                        value={formik.values.paymentMethodId ? { value: formik.values.paymentMethodId, label: getPaymentMethodsList().find((c: any) => c.value === formik.values.paymentMethodId)?.label } : null}
                                        error={formik.errors.paymentMethodId}
                                        isDisabled={!userCan('admin_expirations', 'expirations')}
                                    />
                                    {!userCan('admin_expirations', 'expirations') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los métodos de pago</span>
                                        </div>
                                    )}
                                </div>

                                <RequiredInput
                                    id='date'
                                    type="date"
                                    label="Fecha de Vencimiento"
                                    formik={formik}
                                />

                                <RequiredInput
                                    id='reminderDate'
                                    type="date"
                                    clearable
                                    required={(formik.values?.reminderResponsibleId !== undefined && formik.values.reminderResponsibleId?.length > 6) || formik.values.reminderDate}
                                    onClear={() => {
                                        formik.setFieldValue('reminderDate', '');
                                        formik.setFieldTouched('reminderResponsibleId', false);
                                    }}
                                    label="Fecha de Recordatorio"
                                    formik={formik}
                                />

                                <div className="space-y-2">
                                    <CustomSelect
                                        isSearchable
                                        isClearable
                                        id={'reminderResponsibleId'}
                                        label="Responsable del Recordatorio"
                                        required={formik.values.reminderDate || (formik.values?.reminderResponsibleId !== undefined && formik.values.reminderResponsibleId?.length > 6)}
                                        options={getUsersList()}
                                        onChange={(e: any) => formik.setFieldValue('reminderResponsibleId', e ? e.value : '')}
                                        value={formik.values.reminderResponsibleId ? { value: formik.values.reminderResponsibleId, label: getUsersList().find((c: any) => c.value === formik.values.reminderResponsibleId)?.label } : null}
                                        error={formik.errors.reminderResponsibleId}
                                        isDisabled={!userCan('get_users_for_select', 'user')}
                                    />
                                    {!userCan('get_users_for_select', 'user') && (
                                        <div className="flex items-center gap-2 text-red-500 text-sm">
                                            <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                            <span>No tienes permisos para ver los usuarios</span>
                                        </div>
                                    )}
                                </div>

                                <Textarea
                                    id='notes'
                                    label="Notas"
                                    className="md:col-span-3"
                                    rows={4}
                                    value={formik.values.notes}
                                    onChange={(e) => formik.setFieldValue('notes', e.target.value)}
                                />
                            </FormGroup>
                        </div>
                    </form>
                </div>
            </>
        );
    };

    return (
        <>
            <Page container="fluid">
                {(mode === "Editar" && data) && getContent()}
                {mode === "Crear" && getContent()}
            </Page>
            <FormFooter
                submitBtnText={mode + ' Vencimiento'}
                handleCancelBtn={() => navigate(-1)}
                handleSubmitBtn={formik.submitForm}
                isLoading={isLoading}
            />
        </>
    );
}

export default ExpirationForm;